import React, { useRef, useState, useEffect } from "react";
import { Button, Popover, OverlayTrigger } from "react-bootstrap";
import { useDispatch } from "react-redux";

import { addPromptSectionData, deletePromptSectionData, updatePromptCreationData, updatePromptSectionData, updatePromptSectionOrderData } from "actions/promptEditor";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import _ from "lodash";
import shortid from "shortid";
import { CSS } from "@dnd-kit/utilities";
import cx from "classnames";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";

import styles from "./storyLine.module.scss";


const SectionItem = ({ section, index, isLastItem }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: section.id });

  const [open, setOpen] = useState(true);
  const [editable, setEditable] = useState(false);
  const [localSection, setLocalSection] = useState({
    title: section.name,
    description: section.plan,
  });

  const boxRef = useRef();

  const inputRef = useRef();
  const textareaRef = useRef();

  const dispatch = useDispatch();

  const style = {
    transform: CSS.Transform.toString({
      ...transform,
      scaleX: 1,
      scaleY: 1,
    }),
    transition,
  };

  const onGenerateText = () => {};

  useEffect(() => {
    function handleClickOutside(event) {
      if (!open || !editable) return;

      if (boxRef.current && !boxRef.current.contains(event.target)) {
        setEditable(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, editable]);

  const onDelete = () => {
    dispatch(deletePromptSectionData(section.id));
  };

  const onAddContainer = () => {
    dispatch(addPromptSectionData({
      id: shortid.generate(),
      name: 'Title of the section',
      plan: `Description of the section`,
      order: index + 1,
    }, section.id));
  };

  const onUpdateSection = (id, key, value) => {
    dispatch(updatePromptSectionData(id, key, value));
  }

  const onCloneSection = () => {
    dispatch(addPromptSectionData({
      id: shortid.generate(),
      name: localSection.title,
      plan: localSection.description,
      order: index + 1,
    }, section.id));
  };

  const popover = (isLastItem) => (
    <Popover
      id="ai-optimize-popover"
      className={styles.morePopover}
      style={{
        width: "max-content",
        border: "none",
        boxShadow: "0 4px 10px 0 rgba(0, 0, 0, 0.1)",
      }}
    >
      <Popover.Body style={{ padding: "4px" }}>
        <Button variant="info" onClick={onCloneSection} className={styles.textAi}>
          <i className={cx("cleverstory-clone")} />
        </Button>
        <Button variant="info"  onClick={onDelete} className={styles.textAi} disabled={isLastItem}>
          <i className={cx(styles.icon, "cleverstory-trash")} />
        </Button>
      </Popover.Body>
    </Popover>
  );

  return (
    <>
      <div ref={setNodeRef} style={style} className={styles.section}>
        <div className={styles.dragAndAccordion}>
          {/* DRAG HANDLE ONLY */}
          <div
            className={cx(styles.dragHandle, { [styles.opened]: !open })}
            {...attributes}
            {...listeners}
          >
            <i className="cleverstory-drag" />
          </div>

          <div
            className={cx(styles.accordionIcon, { [styles.opened]: !open })}
            onClick={() => {
              setOpen((prev) => !prev);
            }}
          >
            <i className="cleverstory-fill-right" />
          </div>
        </div>

        <div
          ref={boxRef}
          className={styles.header}
          onClick={open ? () => setEditable(true) : () => {}}
        >
          <div>
            {/* CLICK AREA (Accordion Toggle) */}
            {editable ? (
              <input
                ref={inputRef}
                className={styles.sectionTitle}
                value={localSection.title}
                onBlur={(e) => onUpdateSection(section.id, 'name', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onUpdateSection(section.id, 'name', e.target.value);
                    setEditable(false);
                  }
                }}
                onChange={(e) =>
                  setLocalSection((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }))
                }
                autoFocus
              />
            ) : (
              <h2 className={styles.sectionTitle}>{localSection.title}</h2>
            )}
          </div>
          <div
            className={cx(
              styles.sectionDescriptionWrapper,
              {
                [styles.opened]: open,
              },
              {
                [styles.editable]: editable,
              },
            )}
          >
            {editable ? (
              <textarea
                ref={textareaRef}
                className={styles.sectionDescription}
                value={localSection.description}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onUpdateSection(section.id, 'plan', e.target.value);
                    setEditable(false);
                  }
                }}
                onBlur={(e) => onUpdateSection(section.id, 'plan', e.target.value)}
                onChange={(e) =>
                  setLocalSection((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            ) : (
              <p className={styles.sectionDescription}>
                {localSection.description}
              </p>
            )}
          </div>
        </div>

        <div className={styles.actions}>
          <OverlayTrigger
            trigger="click"
            placement="bottom-end"
            rootClose={true}
            overlay={popover(isLastItem)}
            className={styles.moreWrapper}
          >
            <Button
              variant="info"
              onClick={onGenerateText}
              className={styles.more}
            >
              <i className={cx(styles.icon, "cleverstory-spatial-more")} />
            </Button>
          </OverlayTrigger>
        </div>
      </div>
      <div className={styles.addContainerButtonWrapper}>
        <Button
          variant="info"
          className={styles.addContainerButton}
          onClick={onAddContainer}
        >
          <i className="cleverstory-plus" />
        </Button>
      </div>
    </>
  );
};

export const DraggableAccordionSections = ({ storyLines }) => {
  const dispatch = useDispatch();
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = storyLines.findIndex((s) => s.id === active.id);
      const newIndex = storyLines.findIndex((s) => s.id === over.id);
      dispatch(updatePromptSectionOrderData({ pageStructure: arrayMove(storyLines, oldIndex, newIndex) }));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={storyLines.map((s) => s.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className={styles.container}>
          {storyLines.map((section, index) => (
            <SectionItem
              key={section.id}
              index={index}
              section={section}
              isLastItem={storyLines.length === 1}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};
